import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const TechnologyPage = () => {
  const [activeDemo, setActiveDemo] = useState('wave');
  const [isPlaying, setIsPlaying] = useState(true);
  const canvasRef = useRef(null);
  const animationRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Wave interference demo
  useEffect(() => {
    if (activeDemo === 'wave' && isPlaying && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;

      let time = 0;

      const animate = () => {
        ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Draw wave interference pattern
        for (let x = 0; x < canvas.width; x += 5) {
          for (let y = 0; y < canvas.height; y += 5) {
            const dist1 = Math.sqrt(Math.pow(x - canvas.width * 0.3, 2) + Math.pow(y - canvas.height * 0.5, 2));
            const dist2 = Math.sqrt(Math.pow(x - canvas.width * 0.7, 2) + Math.pow(y - canvas.height * 0.5, 2));
            
            const wave1 = Math.sin(dist1 * 0.05 - time * 0.05);
            const wave2 = Math.sin(dist2 * 0.05 - time * 0.05);
            const interference = (wave1 + wave2) / 2;
            
            const intensity = (interference + 1) * 127.5;
            ctx.fillStyle = `rgba(59, 130, 246, ${intensity / 255})`;
            ctx.fillRect(x, y, 4, 4);
          }
        }

        time++;
        animationRef.current = requestAnimationFrame(animate);
      };

      animate();

      return () => {
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
        }
      };
    }
  }, [activeDemo, isPlaying]);

  const techSpecs = [
    {
      metric: "Processing Latency",
      traditional: "10-100 ms",
      eadc: "< 1 ns",
      improvement: "10,000x"
    },
    {
      metric: "Energy Efficiency",
      traditional: "100 pJ/op",
      eadc: "< 0.1 pJ/op",
      improvement: "1,000x"
    },
    {
      metric: "Parallelism",
      traditional: "10⁶ threads",
      eadc: "∞ (continuous)",
      improvement: "Unbounded"
    },
    {
      metric: "Coherence Distance",
      traditional: "< 1 meter",
      eadc: "Global",
      improvement: "Planetary"
    }
  ];

  const architectureLayers = [
    {
      name: "Application Layer",
      description: "High-level APIs and SDKs for developers",
      components: ["Fluid AI Framework", "Continuous Optimization Library", "Field Computation SDK"]
    },
    {
      name: "Orchestration Layer",
      description: "Intelligent routing between digital and electromagnetic pathways",
      components: ["Task Analyzer", "Path Optimizer", "Coherence Manager"]
    },
    {
      name: "EAPU Core",
      description: "Dual-pathway processing architecture",
      components: ["Cerebral Cortex (Digital)", "Spinal Cord (EM)", "Quantum Bridge"]
    },
    {
      name: "Physical Substrate",
      description: "Electromagnetic field manipulation hardware",
      components: ["Field Modulators", "Phase Detectors", "Coherence Amplifiers"]
    }
  ];

  return (
    <>
      <style>{`
        .tech-hero {
          padding: 10rem 2rem 4rem;
          background: linear-gradient(135deg, var(--gray-900), var(--black));
          color: var(--white);
          position: relative;
          overflow: hidden;
        }

        .tech-hero::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: 
            radial-gradient(circle at 20% 50%, var(--blue-600) 0%, transparent 50%),
            radial-gradient(circle at 80% 50%, var(--purple-600) 0%, transparent 50%);
          opacity: 0.2;
        }

        .tech-hero-content {
          max-width: 1280px;
          margin: 0 auto;
          position: relative;
          z-index: 1;
        }

        .tech-hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 50px;
          font-size: 0.875rem;
          font-weight: 600;
          color: var(--white);
          margin-bottom: 2rem;
        }

        .tech-hero-title {
          font-size: clamp(3rem, 6vw, 4.5rem);
          font-weight: 800;
          line-height: 1.1;
          margin-bottom: 1.5rem;
          letter-spacing: -0.05em;
        }

        .tech-hero-description {
          font-size: 1.375rem;
          color: rgba(255, 255, 255, 0.8);
          line-height: 1.6;
          max-width: 800px;
          margin-bottom: 3rem;
        }

        .tech-hero-actions {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .tech-hero-cta {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 1rem 2rem;
          background: var(--blue-600);
          color: var(--white);
          border-radius: var(--radius);
          text-decoration: none;
          font-weight: 600;
          transition: all var(--transition-base);
        }

        .tech-hero-cta:hover {
          background: var(--blue-500);
          transform: translateY(-2px);
          box-shadow: 0 10px 30px rgba(59, 130, 246, 0.3);
        }

        .tech-hero-cta-secondary {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 1rem 2rem;
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          color: var(--white);
          border-radius: var(--radius);
          text-decoration: none;
          font-weight: 600;
          transition: all var(--transition-base);
        }

        .tech-hero-cta-secondary:hover {
          background: rgba(255, 255, 255, 0.2);
          transform: translateY(-2px);
        }

        /* Interactive Demo Section */
        .demo-section {
          padding: 6rem 2rem;
          background: var(--gray-50);
        }

        .demo-container {
          max-width: 1280px;
          margin: 0 auto;
        }

        .demo-header {
          text-align: center;
          margin-bottom: 4rem;
        }

        .demo-title {
          font-size: 2.5rem;
          font-weight: 800;
          color: var(--gray-900);
          margin-bottom: 1rem;
        }

        .demo-description {
          font-size: 1.125rem;
          color: var(--gray-600);
          max-width: 600px;
          margin: 0 auto;
        }

        .demo-wrapper {
          background: var(--white);
          border-radius: var(--radius-xl);
          box-shadow: var(--shadow-lg);
          overflow: hidden;
        }

        .demo-controls {
          display: flex;
          border-bottom: 1px solid var(--gray-200);
        }

        .demo-control-btn {
          flex: 1;
          padding: 1.5rem;
          background: none;
          border: none;
          font-size: 1rem;
          font-weight: 600;
          color: var(--gray-600);
          cursor: pointer;
          transition: all var(--transition-fast);
          position: relative;
        }

        .demo-control-btn:hover {
          background: var(--gray-50);
        }

        .demo-control-btn.active {
          color: var(--blue-600);
          background: var(--blue-50);
        }

        .demo-control-btn.active::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: var(--blue-600);
        }

        .demo-display {
          padding: 3rem;
          min-height: 400px;
          position: relative;
        }

        .demo-canvas {
          width: 100%;
          height: 400px;
          background: var(--gray-900);
          border-radius: var(--radius-lg);
        }

        .demo-play-controls {
          position: absolute;
          bottom: 2rem;
          right: 2rem;
          display: flex;
          gap: 1rem;
        }

        .play-btn {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: var(--blue-600);
          color: var(--white);
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all var(--transition-fast);
        }

        .play-btn:hover {
          background: var(--blue-500);
          transform: scale(1.1);
        }

        /* Architecture Section */
        .architecture-section {
          padding: 6rem 2rem;
          background: var(--white);
        }

        .architecture-container {
          max-width: 1280px;
          margin: 0 auto;
        }

        .architecture-header {
          text-align: center;
          margin-bottom: 4rem;
        }

        .architecture-title {
          font-size: 2.5rem;
          font-weight: 800;
          color: var(--gray-900);
          margin-bottom: 1rem;
        }

        .architecture-layers {
          display: grid;
          gap: 2rem;
        }

        .layer-card {
          background: var(--gray-50);
          border: 1px solid var(--gray-200);
          border-radius: var(--radius-lg);
          padding: 2rem;
          transition: all var(--transition-base);
          position: relative;
          overflow: hidden;
        }

        .layer-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 4px;
          background: linear-gradient(90deg, var(--blue-600), var(--purple-600));
          transform: translateY(-4px);
          transition: transform var(--transition-base);
        }

        .layer-card:hover::before {
          transform: translateY(0);
        }

        .layer-card:hover {
          box-shadow: var(--shadow-md);
          transform: translateY(-2px);
        }

        .layer-name {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--gray-900);
          margin-bottom: 0.5rem;
        }

        .layer-description {
          font-size: 1rem;
          color: var(--gray-600);
          margin-bottom: 1.5rem;
        }

        .layer-components {
          display: flex;
          gap: 0.75rem;
          flex-wrap: wrap;
        }

        .component-tag {
          padding: 0.5rem 1rem;
          background: var(--white);
          border: 1px solid var(--gray-300);
          border-radius: var(--radius);
          font-size: 0.875rem;
          font-weight: 500;
          color: var(--gray-700);
        }

        /* Specifications Section */
        .specs-section {
          padding: 6rem 2rem;
          background: var(--gray-900);
          color: var(--white);
        }

        .specs-container {
          max-width: 1280px;
          margin: 0 auto;
        }

        .specs-header {
          text-align: center;
          margin-bottom: 4rem;
        }

        .specs-title {
          font-size: 2.5rem;
          font-weight: 800;
          margin-bottom: 1rem;
        }

        .specs-table {
          background: rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: var(--radius-lg);
          overflow: hidden;
        }

        .specs-row {
          display: grid;
          grid-template-columns: 2fr 1fr 1fr 1fr;
          padding: 1.5rem 2rem;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          transition: all var(--transition-fast);
        }

        .specs-row:last-child {
          border-bottom: none;
        }

        .specs-row:hover {
          background: rgba(255, 255, 255, 0.05);
        }

        .specs-row.header {
          background: rgba(255, 255, 255, 0.1);
          font-weight: 700;
          font-size: 0.875rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: rgba(255, 255, 255, 0.7);
        }

        .spec-metric {
          font-weight: 600;
        }

        .spec-improvement {
          color: var(--green-400);
          font-weight: 700;
        }

        /* Roadmap Section */
        .roadmap-section {
          padding: 6rem 2rem;
          background: var(--white);
        }

        .roadmap-container {
          max-width: 1280px;
          margin: 0 auto;
        }

        .roadmap-header {
          text-align: center;
          margin-bottom: 4rem;
        }

        .roadmap-title {
          font-size: 2.5rem;
          font-weight: 800;
          color: var(--gray-900);
          margin-bottom: 1rem;
        }

        .roadmap-timeline {
          position: relative;
          padding: 2rem 0;
        }

        .roadmap-line {
          position: absolute;
          left: 50%;
          top: 0;
          bottom: 0;
          width: 2px;
          background: var(--gray-300);
          transform: translateX(-50%);
        }

        .roadmap-item {
          display: grid;
          grid-template-columns: 1fr auto 1fr;
          gap: 2rem;
          margin-bottom: 4rem;
          align-items: center;
        }

        .roadmap-content {
          background: var(--white);
          border: 1px solid var(--gray-200);
          border-radius: var(--radius-lg);
          padding: 2rem;
          box-shadow: var(--shadow-md);
          transition: all var(--transition-base);
        }

        .roadmap-content:hover {
          box-shadow: var(--shadow-lg);
          transform: translateY(-2px);
        }

        .roadmap-content.left {
          text-align: right;
        }

        .roadmap-dot {
          width: 24px;
          height: 24px;
          background: var(--blue-600);
          border: 4px solid var(--white);
          border-radius: 50%;
          box-shadow: var(--shadow-md);
          z-index: 1;
        }

        .roadmap-date {
          font-size: 0.875rem;
          font-weight: 700;
          color: var(--blue-600);
          margin-bottom: 0.5rem;
        }

        .roadmap-milestone {
          font-size: 1.25rem;
          font-weight: 700;
          color: var(--gray-900);
          margin-bottom: 0.5rem;
        }

        .roadmap-description {
          font-size: 0.95rem;
          color: var(--gray-600);
        }

        @media (max-width: 768px) {
          .tech-hero {
            padding: 8rem 1rem 3rem;
          }

          .tech-hero-actions {
            flex-direction: column;
          }

          .demo-controls {
            flex-direction: column;
          }

          .demo-control-btn {
            text-align: left;
            padding: 1rem 1.5rem;
          }

          .demo-control-btn.active::after {
            width: 3px;
            height: 100%;
            left: 0;
            bottom: auto;
          }

          .specs-row {
            grid-template-columns: 1fr;
            gap: 0.5rem;
          }

          .specs-row.header {
            display: none;
          }

          .specs-row > *::before {
            content: attr(data-label) ": ";
            font-weight: 600;
            color: rgba(255, 255, 255, 0.7);
          }

          .roadmap-line {
            left: 2rem;
          }

          .roadmap-item {
            grid-template-columns: auto 1fr;
            gap: 1rem;
          }

          .roadmap-content.left {
            text-align: left;
          }

          .roadmap-dot {
            margin-left: 1.25rem;
          }
        }
      `}</style>

      <section className="tech-hero">
        <div className="tech-hero-content">
          <div className="tech-hero-badge">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polygon points="12 2 2 7 12 12 22 7 12 2" />
              <polyline points="2 17 12 22 22 17" />
              <polyline points="2 12 12 17 22 12" />
            </svg>
            Revolutionary Architecture
          </div>
          <h1 className="tech-hero-title">
            The Technology Behind Computational Transcendence
          </h1>
          <p className="tech-hero-description">
            Explore the groundbreaking innovations that make Electro-Agnostic Distributed Computing possible. From theoretical foundations to practical implementations, discover how we're redefining the limits of computation.
          </p>
          <div className="tech-hero-actions">
            <Link to="/developers" className="tech-hero-cta">
              Access Developer Portal
              <span>→</span>
            </Link>
            <a href="#demo" className="tech-hero-cta-secondary">
              Try Interactive Demo
              <span>↓</span>
            </a>
          </div>
        </div>
      </section>

      <section id="demo" className="demo-section">
        <div className="demo-container">
          <div className="demo-header">
            <h2 className="demo-title">Interactive EADC Demonstration</h2>
            <p className="demo-description">
              Experience the power of electromagnetic field computation through interactive visualizations
            </p>
          </div>

          <div className="demo-wrapper">
            <div className="demo-controls">
              <button
                className={`demo-control-btn ${activeDemo === 'wave' ? 'active' : ''}`}
                onClick={() => setActiveDemo('wave')}
              >
                Wave Interference
              </button>
              <button
                className={`demo-control-btn ${activeDemo === 'field' ? 'active' : ''}`}
                onClick={() => setActiveDemo('field')}
              >
                Field Computation
              </button>
              <button
                className={`demo-control-btn ${activeDemo === 'quantum' ? 'active' : ''}`}
                onClick={() => setActiveDemo('quantum')}
              >
                Quantum Coherence
              </button>
              <button
                className={`demo-control-btn ${activeDemo === 'network' ? 'active' : ''}`}
                onClick={() => setActiveDemo('network')}
              >
                Network Topology
              </button>
            </div>

            <div className="demo-display">
              {activeDemo === 'wave' && (
                <>
                  <canvas ref={canvasRef} className="demo-canvas" />
                  <div className="demo-play-controls">
                    <button
                      className="play-btn"
                      onClick={() => setIsPlaying(!isPlaying)}
                    >
                      {isPlaying ? (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                          <rect x="6" y="4" width="4" height="16" />
                          <rect x="14" y="4" width="4" height="16" />
                        </svg>
                      ) : (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                          <polygon points="5 3 19 12 5 21 5 3" />
                        </svg>
                      )}
                    </button>
                  </div>
                </>
              )}

              {activeDemo === 'field' && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '400px', background: 'var(--gray-900)', borderRadius: 'var(--radius-lg)' }}>
                  <p style={{ color: 'var(--gray-400)', fontSize: '1.125rem' }}>Field Computation Visualization</p>
                </div>
              )}

              {activeDemo === 'quantum' && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '400px', background: 'var(--gray-900)', borderRadius: 'var(--radius-lg)' }}>
                  <p style={{ color: 'var(--gray-400)', fontSize: '1.125rem' }}>Quantum Coherence Simulation</p>
                </div>
              )}

              {activeDemo === 'network' && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '400px', background: 'var(--gray-900)', borderRadius: 'var(--radius-lg)' }}>
                  <p style={{ color: 'var(--gray-400)', fontSize: '1.125rem' }}>Network Topology Visualization</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="architecture-section">
        <div className="architecture-container">
          <div className="architecture-header">
            <h2 className="architecture-title">EAPU Architecture Layers</h2>
          </div>

          <div className="architecture-layers">
            {architectureLayers.map((layer, index) => (
              <div key={index} className="layer-card">
                <h3 className="layer-name">{layer.name}</h3>
                <p className="layer-description">{layer.description}</p>
                <div className="layer-components">
                  {layer.components.map((component, idx) => (
                    <span key={idx} className="component-tag">{component}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="specs-section">
        <div className="specs-container">
          <div className="specs-header">
            <h2 className="specs-title">Performance Specifications</h2>
          </div>

          <div className="specs-table">
            <div className="specs-row header">
              <div>Metric</div>
              <div>Traditional</div>
              <div>EADC</div>
              <div>Improvement</div>
            </div>
            {techSpecs.map((spec, index) => (
              <div key={index} className="specs-row">
                <div className="spec-metric" data-label="Metric">{spec.metric}</div>
                <div data-label="Traditional">{spec.traditional}</div>
                <div data-label="EADC">{spec.eadc}</div>
                <div className="spec-improvement" data-label="Improvement">{spec.improvement}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="roadmap-section">
        <div className="roadmap-container">
          <div className="roadmap-header">
            <h2 className="roadmap-title">Development Roadmap</h2>
          </div>

          <div className="roadmap-timeline">
            <div className="roadmap-line"></div>
            
            <div className="roadmap-item">
              <div className="roadmap-content left">
                <div className="roadmap-date">Q1 2024</div>
                <div className="roadmap-milestone">EAPU Alpha Release</div>
                <div className="roadmap-description">
                  First functional prototype demonstrating wave interference computation
                </div>
              </div>
              <div className="roadmap-dot"></div>
              <div></div>
            </div>

            <div className="roadmap-item">
              <div></div>
              <div className="roadmap-dot"></div>
              <div className="roadmap-content">
                <div className="roadmap-date">Q3 2024</div>
                <div className="roadmap-milestone">Developer SDK Launch</div>
                <div className="roadmap-description">
                  Complete toolkit for building EADC applications
                </div>
              </div>
            </div>

            <div className="roadmap-item">
              <div className="roadmap-content left">
                <div className="roadmap-date">Q1 2025</div>
                <div className="roadmap-milestone">Commercial Deployment</div>
                <div className="roadmap-description">
                  First production-ready EADC systems for enterprise partners
                </div>
              </div>
              <div className="roadmap-dot"></div>
              <div></div>
            </div>

            <div className="roadmap-item">
              <div></div>
              <div className="roadmap-dot"></div>
              <div className="roadmap-content">
                <div className="roadmap-date">Q3 2025</div>
                <div className="roadmap-milestone">Global Network Launch</div>
                <div className="roadmap-description">
                  Planetary-scale electromagnetic computing infrastructure
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default TechnologyPage;
